import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthApi } from '../services/auth';
import { useAuth } from '../context/AuthContext';
import OnboardingStepper from '../components/OnboardingStepper';

const initialForm = {
  fullName: '',
  email: '',
  password: '',
  confirmPassword: '',
  promoCode: '',
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [inviteToken, setInviteToken] = useState('');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [existingInviteAccount, setExistingInviteAccount] = useState(false);

  useEffect(() => {
    if (existingInviteAccount) {
      setExistingInviteAccount(false);
    }
  }, [form.email, existingInviteAccount]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const promo = params.get('promo');
    if (inviteToken) return;
    if (promo && !form.promoCode) {
      setForm((prev) => ({ ...prev, promoCode: promo.trim() }));
    }
  }, [location.search, form.promoCode, inviteToken]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('invite_token');
    const trimmed = typeof token === 'string' && token.trim() ? token.trim() : '';

    if (!trimmed) {
      setInviteToken('');
      setInviteInfo(null);
      setInviteError(null);
      setInviteLoading(false);
      return;
    }

    setInviteToken(trimmed);
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;

    async function loadInvite() {
      if (!inviteToken) return;
      setInviteLoading(true);
      setInviteError(null);
      try {
        const info = await AuthApi.getPartnerInvite(inviteToken);
        if (cancelled) return;
        setInviteInfo(info);
      } catch (err) {
        if (cancelled) return;
        setInviteInfo(null);
        setInviteError(err?.message || 'Kunne ikke hente invitasjonsdetaljer.');
      } finally {
        if (!cancelled) setInviteLoading(false);
      }
    }

    loadInvite();

    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  useEffect(() => {
    if (!inviteInfo) return;
    setForm((prev) => ({
      ...prev,
      email: inviteInfo.contactEmail || prev.email,
    }));
  }, [inviteInfo]);

  useEffect(() => {
    // Avoid crashing registration in environments where GA/gtag is not loaded.
    try {
      const gtagFn = typeof window !== 'undefined' ? window.gtag : null;
      if (typeof gtagFn !== 'function') return;
      gtagFn('event', 'conversion_event_add_to_cart', {});
    } catch {
      // ignore
    }
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (inviteToken) {
      if (inviteLoading) {
        setError('Vent litt, vi henter invitasjonen din …');
        return;
      }
      if (inviteError) {
        setError(inviteError);
        return;
      }
    }
    if (!form.fullName.trim()) {
      setError('Skriv inn ditt navn.');
      return;
    }
    if (!form.email.trim()) {
      setError('Skriv inn en gyldig e-postadresse.');
      return;
    }
    if (form.password.length < 8) {
      setError('Passordet må være minst 8 tegn.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passordene samsvarer ikke.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const inviteMode = Boolean(inviteToken);

      let result;
      try {
        result = await AuthApi.register(inviteMode ? {
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          password: form.password,
          inviteToken: inviteToken || undefined,
        } : {
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          password: form.password,
          promoCode: form.promoCode.trim() || undefined,
          inviteToken: inviteToken || undefined,
        });
      } catch (registerError) {
        // In some deployments the API might return a transient 502/504 even if the transaction
        // actually succeeded. Try logging in anyway so the user can continue.
        const status = registerError?.status;
        if (status === undefined || status === null || status >= 500) {
          try {
            setSuccess('Logger deg inn …');
            await login(form.email.trim(), form.password);
            if (inviteMode) {
              navigate('/invite/company', { replace: true });
            } else {
              navigate('/companies', { replace: true });
            }
            setForm(initialForm);
            return;
          } catch {
            // fall through to show the original register error
          }
        }
        throw registerError;
      }
      setSuccess(result?.message || 'Kontoen er opprettet! Logger deg inn …');
      await login(form.email.trim(), form.password);
      if (inviteMode) {
        navigate('/invite/company', { replace: true });
      } else {
        navigate('/companies', { replace: true });
      }
      setForm(initialForm);
    } catch (err) {
      if (inviteToken && err?.status === 409) {
        setExistingInviteAccount(true);
        setError('Det finnes allerede en konto med denne e-posten. Logg inn for å fullføre invitasjonen.');
      } else {
        setError(err.message || 'Kunne ikke registrere konto.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const steps = (() => {
    const base = [
      { id: 'account', label: 'Lag konto' },
      { id: 'company', label: 'Opprett selskap' },
    ];
    if (inviteToken && inviteInfo?.requirePaymentMethod) {
      return [...base, { id: 'payment', label: 'Fullfør' }];
    }
    return base;
  })();

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <header className="auth-header">
          <span className="auth-badge">KI Chat</span>
          <h1>Lag konto</h1>
          <p>
            {inviteToken
              ? 'Lag en konto for å fullføre invitasjonen din.'
              : 'Opprett en KI Chat-konto og kom i gang med dine egne selskaper.'}
          </p>
        </header>
        <OnboardingStepper
          steps={steps}
          activeStepId="account"
          completedStepIds={[]}
        />
        <form className="auth-form" onSubmit={handleSubmit}>
          {inviteToken && inviteLoading && (
            <div className="auth-success">Henter invitasjon…</div>
          )}
          {inviteToken && !inviteLoading && inviteInfo && (
            <div className="auth-info">
              Du er invitert av <strong>{inviteInfo.partnerName || 'partner'}</strong>
              {inviteInfo.companyName ? <> for <strong>{inviteInfo.companyName}</strong></> : null}.
            </div>
          )}
          {inviteToken && !inviteLoading && inviteError && (
            <div className="auth-error">⚠️ {inviteError}</div>
          )}
          {inviteToken && existingInviteAccount && (
            <div className="auth-info">
              Har du allerede en konto?{' '}
              <Link to={`/login?invite_token=${encodeURIComponent(inviteToken)}`}>Logg inn her</Link>{' '}
              for å fullføre invitasjonen.
            </div>
          )}

          <label className="auth-label" htmlFor="fullName">
            Fullt navn
            <input
              type="text"
              id="fullName"
              name="fullName"
              className="input"
              value={form.fullName}
              onChange={handleChange}
              placeholder="Ola Nordmann"
              autoComplete="name"
              required
            />
          </label>
          <label className="auth-label" htmlFor="email">
            E-postadresse
            <input
              type="email"
              id="email"
              name="email"
              className="input"
              value={form.email}
              onChange={handleChange}
              placeholder="fornavn@dinbedrift.no"
              autoComplete="email"
              disabled={Boolean(inviteToken && inviteInfo)}
              required
            />
          </label>
          {!inviteToken ? (
            <>
              <label className="auth-label" htmlFor="promoCode">
                Promokode (valgfritt)
                <input
                  type="text"
                  id="promoCode"
                  name="promoCode"
                  className="input"
                  value={form.promoCode}
                  onChange={handleChange}
                  placeholder="F.eks. VELKOMST500"
                />
              </label>
            </>
          ) : null}
          <label className="auth-label" htmlFor="password">
            Passord
            <input
              type="password"
              id="password"
              name="password"
              className="input"
              value={form.password}
              onChange={handleChange}
              placeholder="********"
              autoComplete="new-password"
              required
            />
          </label>
          <label className="auth-label" htmlFor="confirmPassword">
            Bekreft passord
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              className="input"
              value={form.confirmPassword}
              onChange={handleChange}
              placeholder="********"
              autoComplete="new-password"
              required
            />
          </label>
          {error && <div className="auth-error">⚠️ {error}</div>}
          {success && <div className="auth-success">{success}</div>}
          <button type="submit" className="button button-primary" disabled={submitting}>
            {submitting ? 'Oppretter konto…' : 'Registrer konto'}
          </button>
        </form>
        <footer className="auth-footer">
          <p>
            Har du allerede en konto?{' '}
            <Link to={inviteToken ? `/login?invite_token=${encodeURIComponent(inviteToken)}` : '/login'}>Logg inn her</Link>.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default RegisterPage;
